import React, { useEffect, useState } from 'react';
//
import { OrganizationCreate } from '../../types';
import Modal from '@/components/Common/Modal';
import { Button } from '@/components/Common/button';
import { Input } from '@/components/Common/input';
import { Label } from '@/components/Common/label';
import configurationService from '@/services/configurationService';
import { useTranslation } from 'react-i18next';

interface CreateOrganizationModalProps {
  onClose: () => void;
  onSubmit: (data: OrganizationCreate) => void;
}

interface SectorTemplate {
  id: string;
  name: string;
  description: string;
}

const CreateOrganizationModal: React.FC<CreateOrganizationModalProps> = ({
  onClose,
  onSubmit
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<OrganizationCreate>({
    name: '',
    description: '',
    domain: '',
    sector_template_id: '',
    is_active: true
  });
  const [sectorTemplates, setSectorTemplates] = useState<SectorTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    const loadTemplates = async () => {
      try {
        setLoadingTemplates(true);
        const response = await configurationService.getTemplates();
        setSectorTemplates(response.templates ?? []);
      } catch (error) {
        console.error('Error loading sector templates:', error);
      } finally {
        setLoadingTemplates(false);
      }
    };

    loadTemplates();
  }, []);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = t('organizations.modals.create.errors.nameRequired', "Le nom de l'organisation est requis");
    }

    if (formData.domain && !/^(https?:\/\/)?([a-zA-Z0-9.-]+)(\.[a-zA-Z]{2,})(\/.*)?$/.test(formData.domain)) {
      newErrors.domain = t('organizations.modals.create.errors.domainInvalid', 'Format de domaine invalide');
    }

    if (!formData.sector_template_id) {
      newErrors.sectorTemplate = t(
        'organizations.modals.create.errors.sectorRequired',
        "Le secteur de l'organisation est requis"
      );
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const handleChange = (field: keyof OrganizationCreate, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <Modal
      open
      title={t('organizations.modals.create.title', 'Créer une Organisation')}
      onClose={onClose}
      footer={
        <>
          <Button type="button" variant="outline" onClick={onClose}>
            {t('global.cancel')}
          </Button>
          <Button type="submit" form="create-organization-form">
            {t('global.create')}
          </Button>
        </>
      }
    >
      <form id="create-organization-form" onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Label htmlFor="name">
            {t("organizations.modals.create.fields.nameLabel", "Nom de l'organisation *")}
          </Label>
          <Input
            id="name"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            placeholder={t('organizations.modals.create.fields.namePlaceholder', 'Ex: ACME Corporation')}
          />
          {errors.name && (
            <p className="mt-1 text-sm text-destructive">{errors.name}</p>
          )}
        </div>
        <div>
          <Label htmlFor="description">
            {t('organizations.modals.create.fields.descriptionLabel', 'Description')}
          </Label>
          <textarea
            id="description"
            value={formData.description || ''}
            onChange={(e) => handleChange('description', e.target.value)}
            rows={3}
            className="mt-1 block w-full rounded-md border border-border bg-background p-2 text-sm"
            placeholder={t(
              'organizations.modals.create.fields.descriptionPlaceholder',
              "Description de l'organisation...",
            )}
          />
        </div>
        <div>
          <Label htmlFor="domain">
            {t('organizations.modals.create.fields.domainLabel', 'Domaine')}
          </Label>
          <Input
            id="domain"
            value={formData.domain || ''}
            onChange={(e) => handleChange('domain', e.target.value)}
            placeholder={t('organizations.modals.create.fields.domainPlaceholder', 'Ex: acme.com')}
          />
          {errors.domain && (
            <p className="mt-1 text-sm text-destructive">{errors.domain}</p>
          )}
        </div>
        <div>
          <Label htmlFor="sector-template">
            {t('organizations.modals.create.fields.sectorLabel', 'Secteur *')}
          </Label>
          <select
            id="sector-template"
            value={formData.sector_template_id}
            onChange={(e) => {
              handleChange('sector_template_id', e.target.value);
              if (errors.sectorTemplate) {
                setErrors(prev => ({ ...prev, sectorTemplate: '' }));
              }
            }}
            className="mt-1 block h-10 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            disabled={loadingTemplates}
          >
            <option value="">
              {loadingTemplates
                ? t('organizations.modals.create.fields.sectorLoading', 'Chargement des secteurs...')
                : t('organizations.modals.create.fields.sectorPlaceholder', 'Sélectionnez un secteur')}
            </option>
            {sectorTemplates.map((template) => (
              <option key={template.id} value={template.id}>
                {template.name}
              </option>
            ))}
          </select>
          {errors.sectorTemplate && (
            <p className="mt-1 text-sm text-destructive">{errors.sectorTemplate}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="is_active"
            checked={formData.is_active}
            onChange={(e) => handleChange('is_active', e.target.checked)}
            className="h-4 w-4"
          />
          <Label htmlFor="is_active">{t('organizations.activeOrganization')}</Label>
        </div>
      </form>
    </Modal>
  );
};

export default CreateOrganizationModal;
